import { LoggingService } from "./loggingSvc";
import { SdControllerUtilities } from "./sd-controller/sd-controller-utilities";
import { HttpTrackingConfig } from "../../ShellCommon/logger/http-interceptor";
import { ServiceDeskConfig } from "../Startup/serviceDeskConfig";
import { Prerequisite } from "./loading/prerequisite-loading-service";
declare var awa: any;
/**
 * The flighting service is a synchronous service that is guaranteed to have loaded a user's flights before application code is running.
 *  This guarantee is made by the prerequisite service in the SD Shell.
 *  These flights can be maintained at https://flighting.carbon.microsoft.com/Dashboard
 */
export interface FlightingService {
  /**
   * Checks if a specific flight is enabled, returning true if it is, false otherwise.
   *
   * @param flightName The name of the flight to check.
   */
  isFlightEnabled(flightName: string): boolean;

  /**
   * This method gets all enabled flights as an array of strings.
   */
  getAllEnabledFlights(): string[];
}

/**
 * DEPRECATED
 * Structure we expect for implementation of FlightingService
 * These interfaces were added by mistake and there may still be some uses of these interfaces throughout the code.
 */
export interface FrameworkFlightingService {
  isFlightActive(flightName: string): ng.IPromise<boolean>;
  getFlights(): ng.IPromise<string[]>;
}

export interface FrameworkSyncFlightingService {
  isFlightActiveSync(flightName: string): boolean;
  getFlightsSync(): string[];
}

export const flightsStem = "customer/api/Flighting/Flights";

/**
 * FlightingService that acts as an intermediary for gets whether flight should take place based on considtions from SD
 */
export class FlightingServiceImplementation
  implements FlightingService, FlightingService, FrameworkSyncFlightingService
{
  // Flights are currently cached until refresh.
  private flightStatusPromise: ng.IPromise<string[]>;
  private flightStatus: string[];
  private loaded = false;
  private readyDeferral: ng.IDeferred<any>;

  // @ngInject
  constructor(
    private $http: ng.IHttpService,
    private $q: ng.IQService,
    private loggingService: LoggingService,
    private sdControllerUtilities: SdControllerUtilities,
    private sdConfig: ServiceDeskConfig
  ) {
    this.readyDeferral = this.$q.defer();
  }

  public isFlightActive(flightName: string): ng.IPromise<boolean> {
    return this.getFlights().then(
      (data) => {
        return data.indexOf(flightName) != -1;
      },
      (failure) => {
        return false;
      }
    );
  }
  private ensureLoaded() {
    if (!this.loaded) {
      throw Error(
        "Flighting service has not been loaded yet. Cannot call synchronous method. Please make sure there is a prerequisite for this service."
      );
    }
  }

  public whenReady(): ng.IPromise<any> {
    return this.readyDeferral.promise;
  }

  public isFlightActiveSync(flightName: string): boolean {
    this.ensureLoaded();
    return this.flightStatus.includes(flightName);
  }

  public isFlightEnabled(flightName: string): boolean {
    return this.isFlightActiveSync(flightName);
  }

  public getFlights(): ng.IPromise<string[]> {
    if (this.flightStatusPromise) {
      return this.flightStatusPromise;
    }

    return (this.flightStatusPromise = this.getFlightsInternal());
  }

  private getFlightsInternal(): ng.IPromise<string[]> {
    let config: HttpTrackingConfig = {
      headers: {
        "Content-Type": "application/json"
      },
      dependencyOperationName: "Flighting.Flights"
    };
    angular.merge(config, this.sdControllerUtilities.getParametersForUserCacheBusting());
    return this.$http
      .get(this.sdConfig.sdBaseUrl + flightsStem, config)
      .then((success) => {
        this.loaded = true;
        this.flightStatus = <string[]>success.data;
        awa.extendCoreData({ expId: this.flightStatus.map((flight) => "SF:" + flight).join(",") });
        return <string[]>success.data;
      })
      .catch((failure) => {
        let errorMessage = "Request to get flights failed: " + JSON.stringify(failure);
        this.loggingService.logError(errorMessage);
        return this.$q.reject(errorMessage);
      });
  }

  public getFlightsSync(): string[] {
    this.ensureLoaded();
    return this.flightStatus;
  }

  public getAllEnabledFlights(): string[] {
    return this.getFlightsSync();
  }

  public prerequisiteLoadCallback(prerequisite: Prerequisite): ng.IPromise<any> {
    if (!prerequisite.flights) {
      return this.$q.resolve();
    }
    if (
      !angular.isArray(prerequisite.flights) ||
      prerequisite.flights.find((bool: any) => {
        return typeof bool != "boolean";
      })
    ) {
      let error = `malformed prerequisite for flightingSvc. ${prerequisite}`;
      this.readyDeferral.reject(error);
      throw new Error(error);
    }
    if (prerequisite.flights && prerequisite.flights.includes(true)) {
      return this.getFlights()
        .then((value) => {
          this.readyDeferral.resolve(value);
          return value;
        })
        .catch((error) => {
          this.readyDeferral.reject(error);
          return this.$q.reject(error);
        });
    }
    return this.$q.resolve();
  }
}
